/* main.jsx — SPA entry + simple client-side path router.
 *   /admin  · /admin.html   → Host Console (admin_app)
 *   /game                   → Host Console, opening the new-game form
 *   /player · /player.html  → Player app
 *   /                       → landing page (pick an app)
 * A full router (react-router) would be overkill for these routes. Firebase
 * Hosting rewrites all paths to index.html, so deep links land here and this
 * switch picks the app. Each app is lazy-loaded so a player's phone doesn't
 * download the host console (and vice versa). */
import React, { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import './theme.css';
import { initTheme } from './lib/theme.js';
import { ThemeToggle } from './ui/ui.jsx';
import { hostHint, hasPlayerSession } from './lib/hints.js';

initTheme(); // applies the saved [data-theme] before first paint — no flash of the wrong theme

const AdminApp = lazy(() => import('./ui/admin_app.jsx'));
const PlayerRoot = lazy(() => import('./ui/player_app.jsx'));

// ── error boundary: one render crash shows a reload card, not a blank phone ──
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('[app] render crash:', error, info); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#241a11', padding: 24 }}>
        <div style={{ background: '#fbf5e9', borderRadius: 18, padding: '26px 24px', maxWidth: 360, textAlign: 'center', fontFamily: 'system-ui' }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#2c2117' }}>Something went wrong</div>
          <p style={{ fontSize: 14, color: '#7a6a55', lineHeight: 1.5, margin: '8px 0 16px' }}>
            The game itself is safe — scores live on the server, not on this phone. Reload to pick up where you left off.
          </p>
          <button onClick={() => window.location.reload()}
            style={{ padding: '11px 22px', borderRadius: 13, border: 'none', background: '#c98a4b', color: '#1b130c', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}

// ── update banner: a scorekeeper's tab may be open for hours (UPDATE.md rule 4).
// Poll version.json (emitted at build time) and offer — never force — a refresh. ──
function UpdateBanner() {
  const [stale, setStale] = React.useState(false);
  React.useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const { buildId } = await res.json();
        if (buildId && buildId !== __BUILD_ID__) setStale(true);
      } catch { /* offline or dev server — try again next tick */ }
    };
    const t = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  if (!stale) return null;
  return (
    <div style={{ position: 'fixed', bottom: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 999, display: 'flex', gap: 12, alignItems: 'center', background: '#2e231a', color: '#f1e6d2', borderRadius: 999, padding: '9px 10px 9px 18px', boxShadow: '0 6px 20px rgba(0,0,0,0.35)', fontFamily: 'system-ui', fontSize: 13, fontWeight: 600 }}>
      Update available
      <button onClick={() => window.location.reload()}
        style={{ padding: '7px 14px', borderRadius: 999, border: 'none', background: '#c98a4b', color: '#1b130c', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
        Refresh when you’re done
      </button>
    </div>
  );
}

// ── landing: PIN-first, personalized by localStorage hints (never Firebase —
// this bundle stays light; see lib/hints.js). Joining is THE prominent action;
// hosting is a card only when this device has signed in before. ──
function Landing() {
  const [pin, setPin] = React.useState('');
  // read once per mount — hints only change from inside the apps
  const [host] = React.useState(() => hostHint());
  const [playing] = React.useState(() => hasPlayerSession());
  const type = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 4);
    setPin(digits);
    // 4th digit jumps into the player app, which auto-joins (?pin= deep link)
    if (digits.length === 4) window.location.href = '/player?pin=' + digits;
  };
  // theme tokens (switched by ThemeToggle via [data-theme]) with hex fallbacks —
  // hardcoded colors here would make the toggle appear to do nothing
  const card = (href, title, sub) => (
    <a href={href} style={{ display: 'block', textDecoration: 'none', background: 'var(--cream-100, #fbf5e9)', borderRadius: 18, padding: '18px 22px', boxShadow: '0 1px 2px rgba(43,33,23,.05), 0 6px 16px rgba(43,33,23,.07)' }}>
      <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--ink, #2c2117)' }}>{title} →</div>
      <div style={{ fontSize: 13, color: 'var(--ink-soft, #7a6a55)', marginTop: 4, lineHeight: 1.5 }}>{sub}</div>
    </a>
  );
  const quiet = (href, label) => (
    <a href={href} style={{ color: 'rgba(var(--cream-100-rgb, 251,245,233),0.7)', textDecoration: 'none', fontSize: 13.5, fontWeight: 600 }}>{label}</a>
  );
  return (
    <div style={{ width: '100%', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'var(--font-body, system-ui)' }}>
      <div style={{ maxWidth: 400, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <ThemeToggle size={36} />
        </div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <svg width="44" height="44" viewBox="0 0 24 24"><path d="M12 2l7 10-7 10-7-10z" fill="var(--copper, #c98a4b)" /></svg>
          <div style={{ fontWeight: 700, fontSize: 30, color: 'var(--cream, #f1e6d2)', marginTop: 8 }}>Cabin Pinochle</div>
        </div>
        <div style={{ display: 'grid', gap: 12 }}>
          {playing && card('/player', 'Return to your game', 'Pick up right where you left off — nothing was lost.')}

          {/* the hero: join with a PIN, right here */}
          <div style={{ background: 'var(--cream-100, #fbf5e9)', borderRadius: 18, padding: '20px 22px', boxShadow: '0 1px 2px rgba(43,33,23,.05), 0 6px 16px rgba(43,33,23,.07)' }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--ink, #2c2117)' }}>Join a game</div>
            <div style={{ fontSize: 13, color: 'var(--ink-soft, #7a6a55)', marginTop: 4, lineHeight: 1.5 }}>Enter the table PIN — no account needed.</div>
            <input value={pin} onChange={(e) => type(e.target.value)}
              type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={4}
              placeholder="• • • •" aria-label="Join PIN"
              style={{ width: '100%', marginTop: 14, padding: '13px 0', borderRadius: 14, border: '1.5px solid var(--line, #e2d5bd)', background: 'var(--cream, #fffdf8)', textAlign: 'center', fontSize: 26, fontWeight: 700, letterSpacing: 12, color: 'var(--ink, #2c2117)', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
            <div style={{ fontSize: 11.5, color: 'var(--ink-faint, #a89478)', marginTop: 8, textAlign: 'center' }}>joins as soon as the 4th digit goes in</div>
          </div>

          {host && card('/admin', 'My games & tournaments', `Signed in as ${host}.`)}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginTop: 20 }}>
          {host
            ? quiet('/game', 'Play a casual game →')
            : quiet('/admin', 'Hosting? Sign in →')}
        </div>
      </div>
    </div>
  );
}

function pickApp() {
  const path = window.location.pathname.replace(/\.html$/, '');
  if (path === '/admin' || path === '/game') return <AdminApp />;
  if (path === '/player') return <PlayerRoot />;
  return <Landing />;
}

createRoot(document.getElementById('root')).render(
  <ErrorBoundary>
    <Suspense fallback={null}>{pickApp()}</Suspense>
    <UpdateBanner />
  </ErrorBoundary>
);
